import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { useAuth } from '@/features/auth';
import type { TaskFormValues } from '../schemas';

interface StaffMember {
  id: number;
  name: string;
}

interface TaskDialogFormProps {
  form: UseFormReturn<TaskFormValues>;
  defaultMatterId?: number;
  uniqueStaff: StaffMember[];
  onClientSelect: (item: Record<string, unknown> | null) => void;
  onMatterSelect: (item: Record<string, unknown> | null) => Promise<void>;
  onSubmit: (values: TaskFormValues) => Promise<void>;
}

export function TaskDialogForm({
  form,
  defaultMatterId,
  uniqueStaff,
  onClientSelect,
  onMatterSelect,
  onSubmit,
}: TaskDialogFormProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStaff = user?.role?.type === 'STAFF';
  const hasMatter = !!form.watch('matterId');
  const todayStart = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  return (
    <Form {...form}>
      <form
        id="new-task-dialog-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 px-6 py-4"
      >
        {/* Client + Matter (hidden if defaultMatterId provided) */}
        {!defaultMatterId && (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel>{t('selectClient')}</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/clients"
                      labelKey="name"
                      labelKeys={['lastname', 'name', 'middlename']}
                      valueKey="id"
                      selectedId={field.value}
                      onSelectItem={onClientSelect}
                      placeholder={t('selectClient')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="matterId"
              render={({ field }) => (
                <FormItem className="min-w-0">
                  <FormLabel>{t('matter')} *</FormLabel>
                  <FormControl>
                    <UniversalCombobox
                      url="/matters"
                      listUrl="/matters/task-creatable"
                      labelKey="name"
                      valueKey="id"
                      selectedId={field.value}
                      params={{
                        clientId: form.watch('clientId'),
                        limit: 50,
                      }}
                      onSelectItem={onMatterSelect}
                      placeholder={t('selectMatter')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Description */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('description')} *</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('enterDescription')} rows={3} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Due Date */}
        <FormField
          control={form.control}
          name="dueAt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('dueDate')} *</FormLabel>
              <FormControl>
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  mode="date"
                  minDate={todayStart}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Task Type */}
        <FormField
          control={form.control}
          name="typeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('taskType')}</FormLabel>
              <FormControl>
                <UniversalCombobox
                  url="/task-types"
                  labelKey="name"
                  valueKey="id"
                  selectedId={field.value}
                  onSelectItem={(item) => field.onChange(item?.['id'] ?? undefined)}
                  placeholder={t('selectTaskType')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Assigned Staff (hidden for STAFF role — auto-assigned to self) */}
        {hasMatter && !isStaff && (
          <FormField
            control={form.control}
            name="assignedStaffId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('assignedTo')} *</FormLabel>
                <FormControl>
                  <Select
                    value={field.value != null ? String(field.value) : ''}
                    onValueChange={(val) => field.onChange(val ? Number(val) : undefined)}
                    disabled={uniqueStaff.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectStaff')} />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueStaff.length > 0 ? (
                        uniqueStaff.map((staff) => (
                          <SelectItem key={staff.id} value={String(staff.id)}>
                            {staff.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem disabled value="no-staff">
                          {t('noAvailableStaff')}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  );
}
